import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Viva Hayat: Galerie nur ersetzen, wenn die neue Variante ein anderes Bild
    // hat (z.B. Farbwechsel). Bei reinem Größenwechsel bleibt das Bild gleich,
    // das Ersetzen würde nur ein kurzes Neuladen/Flackern erzeugen. Vergleich
    // über die featured_media-ID aus dem Event, NICHT über DOM-Attribute (die
    // werden zur Laufzeit von der Slideshow verändert und sind unzuverlässig).
    const newMediaId = event.detail?.resource?.featured_media?.id;
    if (newMediaId != null) {
      const currentMediaId =
        this.dataset.vhMediaId ||
        this.querySelector('slideshow-slide:not([hidden])')?.getAttribute('slide-id') ||
        '';
      if (String(newMediaId) === String(currentMediaId)) return;
      newMediaGallery.dataset.vhMediaId = String(newMediaId);
    }

    // Viva Hayat (Brief 4.1 "Farbwechsel"): Tausch in eine Same-Document View Transition
    // kapseln, damit die Hero-Box (view-transition-name: vh-hero in vh-transitions.css)
    // in 250 ms ueberblendet statt hart zu springen. Feature-Check, bei Reduced Motion
    // oder ohne Unterstuetzung der heutige direkte Tausch.
    const swap = () => this.replaceWith(newMediaGallery);
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (typeof document.startViewTransition === 'function' && !reduceMotion) {
      try {
        const transition = document.startViewTransition(swap);
        transition.ready?.catch(() => {});
        return;
      } catch (error) {
        /* faellt auf den direkten Tausch zurueck */
      }
    }

    swap();
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
